import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import type { TravelPlan, ConfirmedTravel } from '../types/travel';

export function useTravel() {
  const { user } = useAuthContext();
  const [plans, setPlans] = useState<TravelPlan[]>([]);
  const [confirmedTravel, setConfirmedTravel] = useState<ConfirmedTravel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const updatePlan = async (id: string, updates: Partial<TravelPlan>) => {
    try {
      if (!user?.id) throw new Error('Not authenticated');

      const dbUpdates = {
        title: updates.title,
        description: updates.description,
        location: updates.location,
        start_date: updates.startDate?.toISOString(),
        end_date: updates.endDate?.toISOString(),
        purpose: updates.purpose,
        budget: updates.budget,
        notes: updates.notes,
        status: updates.status
      };

      const { error } = await supabase
        .from('travel_plans')
        .update(dbUpdates)
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;
      await fetchPlans();
    } catch (err) {
      console.error('Error updating travel plan:', err);
      throw err;
    }
  };

  const deletePlan = async (id: string) => {
    try {
      const { error } = await supabase
        .from('travel_plans')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) throw error;
      await fetchPlans();
    } catch (err) {
      console.error('Error deleting travel plan:', err);
      throw err;
    }
  };

  const fetchPlans = async () => {
    try {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from('travel_plans')
        .select('*')
        .eq('user_id', user.id)
        .order('start_date', { ascending: true });

      if (error) throw error;
      
      setPlans(data?.map(plan => ({
          ...plan,
          startDate: new Date(plan.start_date),
          endDate: new Date(plan.end_date),
          created_at: new Date(plan.created_at),
          updated_at: new Date(plan.updated_at)
        })) || []);

    } catch (err) {
      console.error('Error fetching travel plans:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch travel plans'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPlans();
      fetchConfirmedTravel();
    }
  }, [user]);

  const fetchConfirmedTravel = async () => {
    try {
      const { data, error } = await supabase
        .from('confirmed_travel')
        .select('*')
        .eq('user_id', user?.id)
        .order('departure_time', { ascending: true });

      if (error) throw error;
      
      setConfirmedTravel(data?.map(travel => ({
        ...travel,
        departureTime: new Date(travel.departure_time),
        arrivalTime: new Date(travel.arrival_time),
        created_at: new Date(travel.created_at),
        updated_at: new Date(travel.updated_at)
      })) || []);
    } catch (err) {
      console.error('Error fetching confirmed travel:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch confirmed travel'));
    }
  };

  const addConfirmedTravel = async (travel: Omit<ConfirmedTravel, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (!user?.id) throw new Error('Not authenticated');
      
      const dbTravel = {
        user_id: user?.id,
        flight_number: travel.flightNumber,
        airline: travel.airline,
        departure_airport: travel.departureAirport,
        arrival_airport: travel.arrivalAirport,
        departure_time: travel.departureTime.toISOString(),
        arrival_time: travel.arrivalTime.toISOString()
      };

      const { data, error } = await supabase
        .from('confirmed_travel')
        .insert(dbTravel)
        .select()
        .single();

      if (error) throw error;

      // Convert dates for frontend
      const frontendTravel = {
        ...data,
        flightNumber: data.flight_number,
        airline: data.airline,
        departureAirport: data.departure_airport,
        arrivalAirport: data.arrival_airport,
        departureTime: new Date(data.departure_time),
        arrivalTime: new Date(data.arrival_time),
        created_at: new Date(data.created_at),
        updated_at: new Date(data.updated_at)
      };

      await fetchConfirmedTravel();
      return frontendTravel;
    } catch (err) {
      console.error('Error adding confirmed travel:', err);
      throw err;
    }
  };

const addPlan = async (plan: Omit<TravelPlan, 'id' | 'created_at' | 'updated_at'>) => {
  try {
    const dbPlan = {
      user_id: user?.id,
      title: plan.title,
      description: plan.description,
      location: plan.location,
      start_date: plan.startDate.toISOString(),
      end_date: plan.endDate.toISOString(),
      purpose: plan.purpose,
      budget: plan.budget,
      notes: plan.notes,
      status: plan.status || 'planning'
    };

    const { data, error } = await supabase
      .from('travel_plans')
      .insert(dbPlan)
      .select()
      .single();

    if (error) throw error;

    const frontendPlan = {
      ...data,
      startDate: new Date(data.start_date),
      endDate: new Date(data.end_date),
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    };

    await fetchPlans();
    return frontendPlan;
  } catch (err) {
    console.error('Error adding travel plan:', err);
    throw err;
  }
};

  return { 
    plans, 
    confirmedTravel,
    loading, 
    error, 
    addPlan,
    addConfirmedTravel,
    updatePlan,
    deletePlan
  };
}